/**
 * The Copy for Claude Code block on an Issue: everything a developer needs pasted into a
 * Claude Code session — what happened, where, who, and the console and network trails in
 * fenced blocks. The screenshot is not inlined; the block says one exists (and whether it
 * was marked up) and lists any notes the reporter typed on it.
 *
 * Reporter-typed text is fenced as quoted data so it reads as the report, not as
 * instructions to whoever pastes it.
 */

import { CATEGORY_LABELS, PRIORITY_LABELS, STATUS_LABELS } from "@/lib/support-issue-labels";
import type { SupportIssueDetail } from "@/schemas/support-issues";

function quote(text: string): string {
  return text
    .trim()
    .split("\n")
    .map((line) => `> ${line}`)
    .join("\n");
}

export function buildIssueClaudeMarkdown(issue: SupportIssueDetail, issueUrl: string): string {
  const out: string[] = [];
  out.push(`## ${issue.code}: ${issue.title}`, "");
  out.push(
    `- **Status:** ${STATUS_LABELS[issue.status]} · **Priority:** ${PRIORITY_LABELS[issue.priority]}${
      issue.category ? ` · **Category:** ${CATEGORY_LABELS[issue.category]}` : ""
    }`,
  );
  out.push(`- **Reported by:** ${issue.reporterName} (${issue.reporterRole}), ${issue.createdAt}`);
  if (issue.route) out.push(`- **Screen:** \`${issue.route}\``);
  if (issue.diagnostics?.pageUrl) out.push(`- **URL:** ${issue.diagnostics.pageUrl}`);
  if (issue.diagnostics?.appVersion) out.push(`- **App version:** ${issue.diagnostics.appVersion}`);
  if (issue.diagnostics?.userAgent) out.push(`- **Browser:** ${issue.diagnostics.userAgent}`);
  out.push(`- **Issue:** ${issueUrl}`, "");

  out.push("### What happened (reporter's words)", "", quote(issue.description), "");

  if (issue.screenshot.state !== "none") {
    out.push("### Screenshot", "");
    out.push(
      issue.screenshot.markedUp
        ? "A screenshot is attached to the Issue and the reporter marked it up (boxes, arrows or text show what they mean)."
        : "A screenshot is attached to the Issue.",
    );
    if (issue.screenshot.redacted) out.push("Part of it was blacked out by the reporter.");
    if (issue.screenshot.notes.length > 0) {
      out.push("", "Notes the reporter typed on the screenshot:");
      for (const note of issue.screenshot.notes) out.push(`- ${note}`);
    }
    out.push("");
  }

  const comments = issue.timeline.filter((e) => e.kind === "comment");
  if (comments.length > 0) {
    out.push("### Comments", "");
    for (const c of comments)
      out.push(`**${c.actorName ?? "Someone"}** (${c.at}):`, quote(c.text), "");
  }

  const consoleTrail = issue.diagnostics?.consoleTrail ?? [];
  out.push("### Console (last errors and warnings)", "", "```");
  out.push(
    consoleTrail.length > 0
      ? consoleTrail.map((e) => `[${e.at}] ${e.level.toUpperCase()} ${e.message}`).join("\n")
      : "(none captured)",
  );
  out.push("```", "");

  const network = issue.diagnostics?.networkTrail ?? [];
  out.push("### Network (last API calls and navigations)", "", "```");
  out.push(
    network.length > 0
      ? network
          .map((e) =>
            e.kind === "navigation"
              ? `[${e.at}] NAV ${e.path}`
              : `[${e.at}] ${e.method ?? "GET"} ${e.path} → ${e.status ?? "failed"}${
                  e.durationMs !== undefined ? ` (${e.durationMs}ms)` : ""
                }`,
          )
          .join("\n")
      : "(none captured)",
  );
  out.push("```");
  return out.join("\n");
}
